import type { FilterSelection } from '@/app/common/filters/core/filtersTypes.ts';
import type {
  FilterSessionEntry,
  FilterSessionState,
  FilterSessionValue,
} from '@/app/common/filters/session/sessionState.ts';
import {
  SESSION_ENTRY_LIMIT,
  RECENTLY_USED_LIMIT,
  sameSessionValue,
} from '@/app/common/filters/session/sessionState.ts';

const selectedValuesOf = (selections: FilterSelection[], groupId: string): string[] =>
  selections
    .filter((selection) => selection.groupId === groupId && selection.selected)
    .map((selection) => selection.value);

const firstPressedIn = (pressed: FilterSessionValue[], groupId: string, selected: string[]): string | undefined =>
  pressed.find((value) => value.groupId === groupId && selected.includes(value.value))?.value;

const recordGroup = (
  entries: FilterSessionEntry[],
  groupId: string,
  selected: string[],
  pressed: FilterSessionValue[]
): FilterSessionEntry[] => {
  if (!selected.length) return entries.filter((entry) => entry.groupId !== groupId);

  const existing = entries.find((entry) => entry.groupId === groupId);
  const leader =
    existing?.leader && selected.includes(existing.leader)
      ? existing.leader
      : (firstPressedIn(pressed, groupId, selected) ?? selected[0]);

  return existing
    ? entries.map((entry) => (entry.groupId === groupId ? { groupId, leader } : entry))
    : [...entries, { groupId, leader }];
};

export const promoteValues = (previous: FilterSessionValue[], fresh: FilterSessionValue[]): FilterSessionValue[] => [
  ...fresh,
  ...previous.filter((value) => !fresh.some((one) => sameSessionValue(one, value))),
];

export const nextFilterSession = (
  previous: FilterSessionState,
  selections: FilterSelection[],
  justUsed: FilterSessionValue[] = [],
  pressed: FilterSessionValue[] = []
): FilterSessionState => {
  const touched = [...new Set(selections.map((selection) => selection.groupId))];
  const entries = touched.reduce(
    (result, groupId) => recordGroup(result, groupId, selectedValuesOf(selections, groupId), pressed),
    previous.entries
  );

  return {
    entries: entries.slice(-SESSION_ENTRY_LIMIT),
    recentlyUsed: promoteValues(previous.recentlyUsed, justUsed).slice(0, RECENTLY_USED_LIMIT),
  };
};
